import React from "react";
import { connect } from "react-redux";
import { Input, Select } from "../Input/index.js";
import language from "../../language/index.js";
import { Button } from "../Button/index.js";
import api from "../../utils/api/index.js";
import { fetchRejectionStatus } from "../../utils/queries/rejectionStatus.js";
import toastMessage from "../../utils/toastMessage.js";
import { fetchReturnedNumber } from "../../utils/queries/return-product.js";
import { generateShipmentNumber } from "../../utils/random.js";
import ShippingQueue from "../Requests/shipping/ShippingQueue.js";
import { getStorage } from "../../utils/storage.js";
import { defineRoleCenterSite } from "../../utils/handleAccessRoles.js";
import { fetchInventories } from "../../utils/queries/production/inventoryQuery.js";
import { fetchBloodBanks } from "../../utils/queries/bloodBank.js";
import fetchHospitals from "../../utils/queries/hospitalQuery.js";
import fetchDepartments from "../../utils/queries/departmentQuery.js";
import { fetchReturnedProducts } from "../../utils/queries/return-product.js";

// A facility supplied by a Zipline hub cannot send a return back to it: a
// drone port holds no stock to receive one. Those returns go to Kigali IH
// instead. A facility supplied by anything else is left alone, so a hospital
// like CHUK keeps the blood bank it is already configured against.
//
// Matched on code first, with the name kept as a fallback: a blood bank can
// be renamed from the UI, but its code is what identifies it in the data.
const ZIPLINE_SUPPLIER_CODES = ["0100", "0101"];
const ZIPLINE_SUPPLIER_NAMES = ["zipline muhanga", "zipline kayonza"];
const ZIPLINE_RETURN_DESTINATION_CODE = "0002";
const ZIPLINE_RETURN_DESTINATION_NAME = "kigali ih distribution bb";

// A blood bank reaches this screen under two labels: the dropdown query
// prefixes the code (0102-CHUK LAB) while a hospital allowed-blood-bank entry
// carries the bare name. Strip the prefix so the two can be compared.
const normaliseFacilityName = (name) =>
  String(name || "")
    .replace(/^\s*[0-9]+\s*-\s*/, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const normaliseFacilityCode = (code) => String(code || "").trim();

class ReturnRequest extends React.Component {
  state = {
    error: {},
    data: [],
    isDoneSearching: false,
    product: "",
    donationNumber: "",
    requestNo: "",
    returnNumber: "",
    comment: "",
    user: {},
    source: null,
    destination: null,
    isFromWard: null,
    isLookingUp: false,
    facilityOptions: [],
    serviceOptions: [],
    isLoadingServices: false,
    hospitalAllowedMap: {},
    allowedDestinations: null,
  
    durationOutOfCondition: "",
    hasBloodOpen: null,
    reason: null,
};

  componentDidMount = async () => {
    const user = await getStorage();
    this.setState({ user });
    this.getReturnNumber();

    await this.preloadFacilityOptions();

    if (this.props.selectedData?._id) {
      this.populateFromSelectedData();
    } else if (!!this.props.isForNominative) {
      this.setState({
        donationNumber: this.props.donationNumber,
        product: this.props.product,
      });
    }
  };

  populateFromSelectedData = async () => {
    const sd = this.props.selectedData;
    const lang = this.props.defaultLanguage;

    const toOption = (obj) =>
      obj ? { label: obj.name, value: obj._id, _id: obj._id, name: obj.name } : null;
    const toSelectOpt = (val) =>
      val ? { label: val, value: val } : null;

    const isFromWardBool = sd.isFromWard;
    const isFromWard = isFromWardBool != null
      ? { label: isFromWardBool ? language[lang].yes : language[lang].no, value: isFromWardBool ? "yes" : "no" }
      : null;

    // Base state from the return-request record
    const base = {
      returnNumber: sd.returnNumber || "",
      comment: sd.comment || "",
      isFromWard,
      source: toOption(sd.from),
      destination: toOption(sd.to),
    };

    this.setState(base, () => {
      if (isFromWardBool) this.loadServiceOptions();
    });

    // Fetch the first child return-product record to get product-level fields
    try {
      const res = await fetchReturnedProducts(lang, { page: 1, limit: 1, returnId: sd._id });
      const child = res?.data?.[0];
      if (child) {
        const reason = child.reason
          ? { label: child.reason.name || child.reason, value: child.reason._id || child.reason, _id: child.reason._id }
          : null;
        this.setState({
          donationNumber: child.donationNumber || child.donation?.donationNumber || "",
          product: child.product || child.inventory?.code || "",
          durationOutOfCondition: toSelectOpt(child.durationOutOfCondition),
          hasBloodOpen: toSelectOpt(child.hasBloodOpen),
          reason,
          // override isFromWard if present on child
          isFromWard: child.isFromWard != null
            ? { label: child.isFromWard ? language[lang].yes : language[lang].no, value: child.isFromWard ? "yes" : "no" }
            : isFromWard,
        });
      }
    } catch (_) {}
  };

  preloadFacilityOptions = async () => {
    const [bbResult, hospResult] = await Promise.allSettled([
      fetchBloodBanks(this.props.defaultLanguage, { page: 1, limit: 200, type: "dropdown" }),
      fetchHospitals(this.props.defaultLanguage, { page: 1, limit: 200, isDirectTransfer: true }),
    ]);

    const bloodBanks = bbResult.status === "fulfilled" && Array.isArray(bbResult.value)
      ? bbResult.value : [];

    const rawHospitals =
      hospResult.status === "fulfilled" && hospResult.value?.data
        ? hospResult.value.data
        : [];

    const hospitalStocks = rawHospitals
      .filter((h) => h.bloodBank?._id)
      .map((h) => ({ label: h.bloodBank.name, value: h.bloodBank._id, _id: h.bloodBank._id }));

    // Merge, deduplicating by value/_id so a blood bank that is also a
    // hospital's stock doesn't appear twice.
    const seen = new Set(bloodBanks.map((b) => b.value));
    const merged = [
      ...bloodBanks,
      ...hospitalStocks.filter((s) => !seen.has(s.value)),
    ];

    // Build a lookup: hospital's own bloodBank._id → allowed destination BBs.
    // This comes from the hospital record itself, not the user session, so it
    // always reflects the current hospital configuration.
    const hospitalAllowedMap = {};
    rawHospitals.forEach((h) => {
      if (!h.bloodBank?._id) return;
      const toOpt = (bb) => {
        if (!bb) return null;
        const id = bb._id || bb;
        const name = bb.name;
        return id && name ? { label: name, value: id, _id: id } : null;
      };
      const multi = Array.isArray(h.multipleAllowedBloodBank)
        ? h.multipleAllowedBloodBank.map(toOpt).filter(Boolean)
        : [];
      const single = toOpt(h.allowedBloodBank);
      // Union of both fields, deduped by id
      const dedupedById = new Map();
      [...multi, ...(single ? [single] : [])].forEach((o) => dedupedById.set(o.value, o));
      hospitalAllowedMap[h.bloodBank._id] = Array.from(dedupedById.values());
    });

    this.setState({ facilityOptions: merged, hospitalAllowedMap });
  };

  buildAllowedDestinations = () => {
    const { user, hospitalAllowedMap } = this.state;
    if (!user?.stock || !hospitalAllowedMap) return [];
    return this.redirectZiplineDestinations(
      hospitalAllowedMap[user.stock] || []
    );
  };

  // Swap a Zipline hub for the blood bank that actually receives its returns,
  // deduping so a facility allowed both the hub and that blood bank is not
  // offered it twice.
  //
  // If that blood bank is not among the facilities this screen loaded there is
  // nothing to redirect to, and the list is handed back untouched. The hub
  // then stands as it does today: a destination that has to be corrected by
  // hand is a worse outcome than one that cannot be chosen at all, because a
  // destination is required before the return can be submitted.
  // An allowed-destination entry carries only a blood bank name and id. The
  // code lives on the facility list this screen loaded, so look it up by id
  // rather than deciding on the name alone.
  facilityCodeFor = (option) => {
    if (option?.code) return normaliseFacilityCode(option.code);

    const id = option?.value || option?._id;

    const match = (this.state.facilityOptions || []).find(
      (opt) => (opt.value || opt._id) === id
    );

    return normaliseFacilityCode(match?.code);
  };

  isZiplineSupplier = (option) => {
    const code = this.facilityCodeFor(option);
    const name = normaliseFacilityName(option?.label || option?.name);

    return (
      (!!code && ZIPLINE_SUPPLIER_CODES.includes(code)) ||
      ZIPLINE_SUPPLIER_NAMES.includes(name)
    );
  };

  redirectZiplineDestinations = (allowed) => {
    if (!allowed.some(this.isZiplineSupplier)) return allowed;

    const target = (this.state.facilityOptions || []).find(
      (opt) =>
        normaliseFacilityCode(opt.code) === ZIPLINE_RETURN_DESTINATION_CODE ||
        normaliseFacilityName(opt.label) === ZIPLINE_RETURN_DESTINATION_NAME
    );

    if (!target) return allowed;

    const seen = new Set();

    return allowed.reduce((out, opt) => {
      const resolved = this.isZiplineSupplier(opt) ? target : opt;

      if (seen.has(resolved.value)) return out;

      seen.add(resolved.value);
      out.push(resolved);

      return out;
    }, []);
  };

  loadServiceOptions = async () => {
    this.setState({ isLoadingServices: true });
    let loaded = [];
    try {
      const result = await fetchDepartments(this.props.defaultLanguage, {
        type: "dropdown",
      });
      loaded = Array.isArray(result) ? result : [];
      this.setState({ serviceOptions: loaded });
    } catch (_) {}
    this.setState({ isLoadingServices: false });
    return loaded;
  };

  getReturnNumber = async () => {
    if (this.props.selectedData?._id) return;
    try {
      this.setState({ isFetchingReturnNumber: true });
      const data = await fetchReturnedNumber();
      this.setState({
        isFetchingReturnNumber: false,
        returnNumber: generateShipmentNumber(data?.returnNumber),
      });
    } catch (error) {
      toastMessage("error", error);
      this.setState({ isFetchingReturnNumber: false });
    }
  };

  validateForm() {
    let { error, donationNumber, product, reason, durationOutOfCondition, returnNumber, isFromWard, source } = this.state;

    if (!returnNumber) {
      error.returnNumber = language[this.props.defaultLanguage].field_required;
    }
    if (!donationNumber) {
      error.donationNumber = language[this.props.defaultLanguage].donation_number_required;
    }
    if (!durationOutOfCondition) {
      error.durationOutOfCondition = language[this.props.defaultLanguage].field_required;
    }
    if (!product) {
      error.product = language[this.props.defaultLanguage].product_required;
    }
    if (!reason) {
      error.reason = language[this.props.defaultLanguage].reason_required;
    }
    if (!isFromWard) {
      error.isFromWard = language[this.props.defaultLanguage].field_required;
    }
    if (!source) {
      error.source = language[this.props.defaultLanguage].field_required;
    }
    if (!this.state.destination) {
      error.destination = language[this.props.defaultLanguage].field_required;
    }

    this.setState({ error });
  }

  onSubmit = async () => {
    await this.validateForm();

    try {
      if (Object.keys(this.state.error).length > 0) return;

      const { donationNumber, product, reason, durationOutOfCondition, hasBloodOpen, comment, returnNumber } = this.state;
      const isEditing = !!this.props.selectedData?._id;

      this.setState({ isSubmitting: true });

      const requestBody = {
        product,
        donationNumber,
        reasonId: reason?._id,
        durationOutOfCondition: durationOutOfCondition?.value,
        hasBloodOpen: hasBloodOpen?.value,
        comment,
        returnNumber,
        status: "pending",
        isNominative: this.props.isForNominative,
        isFromWard: this.state.isFromWard?.value === "yes",
        source: this.state.source?._id || this.state.source?.value || undefined,
        destination: this.state.destination?._id || this.state.destination?.value || undefined,
      };

      if (isEditing) {
        requestBody.id = this.props.selectedData._id;
      }

      await api({
        method: isEditing ? "PUT" : "POST",
        url: `${process.env.REACT_APP_BBMS_BASE_API}/return-product`,
        data: requestBody,
      });

      this.setState({ isSubmitting: false, isRefreshing: true, error: {} });

      toastMessage("success", language[this.props.defaultLanguage][isEditing ? "update_data_success" : "add_data_success"]);

      if (isEditing) {
        this.props.getData && this.props.getData(true);
        this.props.handleCloseModal && this.props.handleCloseModal();
        return;
      }

      this.setState({
        donationNumber: "",
        product: "",
        reason: null,
        source: null,
        destination: null,
        isFromWard: null,
        durationOutOfCondition: null,
        hasBloodOpen: null,
        comment: "",
        returnNumber: "",
        allowedDestinations: null,
      });
      this.getReturnNumber();
    } catch (error) {
      this.setState({ isSubmitting: false });
      toastMessage("error", error);
    }
  };

  onChangeText = async (field, e) => {
    let { error } = this.state;
    let inputValue = e && e?.target ? e?.target?.value : e;

    delete error[field];

    this.setState({ [field]: inputValue, isDoneSearching: false }, () => {
      if (field === "donationNumber" || field === "product") {
        this.scheduleProductLookup();
      }
      if (field === "donationNumber" && this.state.isFromWard?.value === "yes") {
        this.scheduleServiceLookup();
      }
    });
  };

  // Typing a 7-digit donation number used to fire a lookup per keystroke, so
  // requests for partial numbers ("6", "61", "616"…) raced the complete one.
  // Any of those finding nothing would set the error after the good response
  // had cleared it, leaving "Product not found in your current stock" on a
  // product that is in stock. Wait for typing to settle before looking up.
  scheduleProductLookup = () => {
    clearTimeout(this.lookupTimer);

    // Drop any previous "not found" the instant either field changes. Without
    // this the old message sits under a value the user has already corrected
    // until the next lookup settles, which is what made typing feel broken
    // while pasting looked fine.
    if (this.state.error?.donationNumber) {
      const error = { ...this.state.error };
      delete error.donationNumber;
      this.setState({ error });
    }

    this.lookupTimer = setTimeout(() => this.lookupProductInfo(), 600);
  };

  scheduleServiceLookup = () => {
    clearTimeout(this.serviceLookupTimer);
    this.serviceLookupTimer = setTimeout(() => this.lookupServiceFromDonation(), 600);
  };

  lookupServiceFromDonation = async (passedServices) => {
    const { donationNumber, product, isFromWard } = this.state;
    const serviceOptions = passedServices || this.state.serviceOptions;
    if (!donationNumber || isFromWard?.value !== "yes") return;

    const matchService = (serviceId, serviceName) => {
      return (serviceOptions || []).find(
        (opt) =>
          (serviceId && opt.value === serviceId) ||
          (serviceName && opt.label?.toLowerCase() === serviceName.toLowerCase())
      );
    };

    try {
      const user = await getStorage();
      const API_URL = process.env.REACT_APP_BBMS_BASE_API;
      const headers = { Authorization: `Bearer ${user.token}` };

      // Step 1: find the inventory record by donation number.
      const invRes = await api({
        method: "GET",
        url: `${API_URL}/inventory`,
        params: { donationNumber, code: product || undefined, page: 1, limit: 1 },
        headers,
      });
      const inv = invRes?.data?.data?.[0] || invRes?.data?.[0];
      if (!inv) return;

      // Step 1b: the inventory record itself may carry the service it was
      // issued to (some backends populate this on distribution).
      if (inv.service) {
        const svcId = inv.service._id || inv.service;
        const svcName = inv.service.name;
        const m = matchService(svcId, svcName);
        if (m) { this.setState({ source: m }); return; }
      }

      if (!inv._id) return;

      // Step 2: find the shipment that carried this inventory to a service.
      const shipRes = await api({
        method: "GET",
        url: `${API_URL}/shipping-queue`,
        params: { page: 1, limit: 1, inventory: inv._id },
        headers,
      });
      const shipment = shipRes?.data?.data?.[0] || shipRes?.data?.[0];
      if (!shipment?.service) return;

      const serviceId = shipment.service._id || shipment.service;
      const serviceName = shipment.service.name;

      const matched = matchService(serviceId, serviceName);
      if (matched) this.setState({ source: matched });
    } catch (_) {
      // silently fail — user can select the service manually
    }
  };

  componentWillUnmount() {
    clearTimeout(this.lookupTimer);
    clearTimeout(this.serviceLookupTimer);
  }

  lookupProductInfo = async () => {
    const { donationNumber, product, user, isFromWard } = this.state;
    if (!donationNumber || !product) return;

    // Ward/service returns: no stock to check on the frontend — backend handles it
    if (isFromWard?.value === "yes") return;

    // Debouncing alone cannot guarantee ordering, so each lookup is tagged and
    // only the newest one is allowed to write to state.
    const lookupId = (this.lookupSeq = (this.lookupSeq || 0) + 1);

    this.setState({ isLookingUp: true });
    let { error } = this.state;
    delete error.donationNumber;
    this.setState({ error });

    try {
      const { hospitalRole } = await defineRoleCenterSite();
      const params = { donationNumber, code: product, page: 1, limit: 1 };
      if (user.stock && !hospitalRole) params.stock = user.stock;

      const result = await fetchInventories(this.props.defaultLanguage, params);

      // A newer lookup started, or the fields changed while this was in
      // flight — this answer is about a value the user has moved on from.
      if (
        lookupId !== this.lookupSeq ||
        this.state.donationNumber !== donationNumber ||
        this.state.product !== product
      ) {
        this.setState({ isLookingUp: false });
        return;
      }

      const inv = result?.data?.[0];

      if (!inv) {
        this.setState({
          error: {
            ...this.state.error,
            donationNumber:
              language[this.props.defaultLanguage].product_not_in_stock ||
              "Product not found in your current stock",
          },
        });
      } else if (inv.status === "transfused") {
        this.setState({
          error: {
            ...this.state.error,
            donationNumber:
              language[this.props.defaultLanguage].transfused_blood_cannot_be_returned ||
              "Transfused blood cannot be returned",
          },
        });
      }
    } catch (_) {}

    this.setState({ isLookingUp: false });
  };

  getReturnStatus = async (isLoading, search = {}) => {
    try {
      this.setState({ isLoading });
      const { data } = await fetchRejectionStatus("dropdown", {
        page: 0,
        limit: 50,
        ...search,
      });
      this.setState({ isLoading: false });
      return data;
    } catch (error) {
      toastMessage("error", error);
    }
  };

  getDestinationOptions = async (search = "") => {
    try {
      const [bloodBanksRes, hospitalsRes] = await Promise.all([
        fetchBloodBanks(this.props.defaultLanguage, { page: 1, limit: 100, type: "dropdown", q: search || undefined }),
        fetchHospitals(this.props.defaultLanguage, { page: 1, limit: 100, type: "dropdown", isDirectTransfer: true, q: search || undefined }),
      ]);

      return [
        ...(Array.isArray(bloodBanksRes) ? bloodBanksRes : []),
        ...(Array.isArray(hospitalsRes) ? hospitalsRes : []),
      ];
    } catch (_) {
      return [];
    }
  };

  render() {
    const { isFromWard, serviceOptions, isLoadingServices, facilityOptions, allowedDestinations } = this.state;

    return (
      <>
        <div className="row g-3">
          <div className="col-12 col-sm-6 col-md-4">
            <Input
              label={language[this.props.defaultLanguage].return_number}
              value={this.state.returnNumber}
              onChange={(e) => this.onChangeText("returnNumber", e)}
              error={this.state.error.returnNumber}
              isLoading={this.state.isFetchingReturnNumber}
              disabled
            />
          </div>
          <div className="col-12 col-sm-6 col-md-4">
            <Input
              placeholder={language[this.props.defaultLanguage].donation_number_placeholder}
              label={language[this.props.defaultLanguage].donation_number}
              value={this.state.donationNumber}
              onChange={(e) => this.onChangeText("donationNumber", e)}
              error={this.state.error.donationNumber}
              disabled={this.props.isForNominative}
            />
          </div>
          <div className="col-12 col-sm-6 col-md-4">
            <Input
              placeholder={language[this.props.defaultLanguage].product_placeholder}
              label={language[this.props.defaultLanguage].product}
              value={this.state.product}
              onChange={(e) => this.onChangeText("product", e)}
              error={this.state.error.product}
              disabled={this.props.isForNominative}
            />
          </div>
          <div className="col-12 col-sm-6 col-md-4">
            <Select
              options={[
                { label: language[this.props.defaultLanguage].yes, value: "yes" },
                { label: language[this.props.defaultLanguage].no, value: "no" },
              ]}
              placeholder={language[this.props.defaultLanguage].select}
              label={language[this.props.defaultLanguage].is_return_from_ward}
              value={isFromWard}
              onChange={(e) => {
                let autoSource = null;
                let autoDestination = null;
                let allowedDestinations = null;

                const userStockOpt = this.state.user?.stock
                  ? this.state.facilityOptions.find(
                      (opt) => (opt.value || opt._id) === this.state.user.stock
                    ) || null
                  : null;

                if (e?.value === "no") {
                  autoSource = userStockOpt;
                  if (autoSource) {
                    const allowed = this.buildAllowedDestinations();
                    if (allowed.length > 0) {
                      autoDestination = allowed[0];
                      if (allowed.length > 1) allowedDestinations = allowed;
                    }
                  }
                }

                if (e?.value === "yes") {
                  // Ward return goes back to the hospital's own blood bank
                  autoDestination = userStockOpt;
                }

                this.setState({
                  isFromWard: e,
                  source: autoSource,
                  destination: autoDestination,
                  allowedDestinations,
                }, () => {
                  if (e?.value === "yes") {
                    this.loadServiceOptions().then((services) => {
                      if (this.state.donationNumber) {
                        this.lookupServiceFromDonation(services);
                      }
                    });
                  }
                });
              }}
              error={this.state.error.isFromWard}
            />
          </div>
          {isFromWard?.value === "yes" && (
            <div className="col-12 col-sm-6 col-md-4">
              <Select
                options={serviceOptions}
                placeholder={language[this.props.defaultLanguage].select}
                label={
                  <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {language[this.props.defaultLanguage].select_ward_service}
                    {isLoadingServices && (
                      <span
                        className="loader"
                        style={{ width: 18, height: 18, borderWidth: 2, flexShrink: 0 }}
                      />
                    )}
                  </span>
                }
                value={this.state.source}
                onChange={(e) => this.onChangeText("source", e)}
                error={this.state.error.source}
                isLoading={isLoadingServices}
                disabled={!!this.state.source}
              />
            </div>
          )}
          {isFromWard?.value === "no" && (
            <div className="col-12 col-sm-6 col-md-4">
              <Select
                options={facilityOptions}
                placeholder={language[this.props.defaultLanguage].select}
                label={language[this.props.defaultLanguage].select_source_facility}
                value={this.state.source}
                onChange={(e) => this.onChangeText("source", e)}
                error={this.state.error.source}
                isLoading={facilityOptions.length === 0}
                disabled
              />
            </div>
          )}
          <div className="col-12 col-sm-6 col-md-4">
            {allowedDestinations && (
              <p className="text-muted" style={{ fontSize: "0.8rem", marginBottom: 4 }}>
                Choose the right destination — it must be the blood bank this product originally came from.
              </p>
            )}
            <Select
              {...(allowedDestinations
                ? { options: allowedDestinations }
                : { loadOptions: (e) => this.getDestinationOptions(e) })}
              placeholder={language[this.props.defaultLanguage].select}
              label={language[this.props.defaultLanguage].destination}
              required
              value={this.state.destination}
              onChange={(e) => this.onChangeText("destination", e)}
              error={this.state.error.destination}
              disabled={isFromWard?.value === "yes" || (isFromWard?.value === "no" && !allowedDestinations)}
            />
          </div>
          <div className="col-12 col-sm-6 col-md-4">
            <Select
              options={[
                { label: "0-30 Min", value: "0-30 Min" },
                { label: "31-60 Min", value: "31-60 Min" },
                { label: ">60 Min", value: ">60 Min" },
              ]}
              placeholder={language[this.props.defaultLanguage].select}
              label={language[this.props.defaultLanguage].how_long_out_of_condition}
              value={this.state.durationOutOfCondition}
              onChange={(e) => this.onChangeText("durationOutOfCondition", e)}
              error={this.state.error.durationOutOfCondition}
            />
          </div>
          <div className="col-12 col-sm-6 col-md-4">
            <Select
              options={[
                { label: language[this.props.defaultLanguage].yes, value: "yes" },
                { label: language[this.props.defaultLanguage].no, value: "no" },
                { label: language[this.props.defaultLanguage].not_known, value: "Not known" },
              ]}
              placeholder={language[this.props.defaultLanguage].select}
              label={language[this.props.defaultLanguage].has_blood_open}
              value={this.state.hasBloodOpen}
              onChange={(e) => this.onChangeText("hasBloodOpen", e)}
              error={this.state.error.hasBloodOpen}
            />
          </div>
          <div className="col-12 col-md-8">
            <Select
              loadOptions={(e) => this.getReturnStatus(true, { q: e, type: "RETURN" })}
              placeholder={language[this.props.defaultLanguage].reason_placeholder}
              label={language[this.props.defaultLanguage].reason}
              value={this.state.reason}
              onChange={(e) => this.onChangeText("reason", e)}
              error={this.state.error.reason}
            />
          </div>
          <div className="col-12">
            <Input
              placeholder={language[this.props.defaultLanguage].comment_placeholder}
              label={language[this.props.defaultLanguage].comment}
              value={this.state.comment}
              onChange={(e) => this.onChangeText("comment", e)}
              error={this.state.error.comment}
              textarea
            />
          </div>
          <div className="col-12 d-flex align-items-center gap-2">
            {this.props.handleCloseModal && (
              <Button
                className="btn-default"
                text={language[this.props.defaultLanguage].cancel}
                onPress={this.props.handleCloseModal}
              />
            )}
            <Button
              text={language[this.props.defaultLanguage].submit}
              isSubmitting={this.state.isSubmitting}
              onPress={this.onSubmit.bind(this)}
            />
          </div>
        </div>
        {!this.props.selectedData?._id && this.state.returnNumber && this.state.returnNumber !== "" && (
          <div className="card col-md-12">
            <div className="card-body">
              <ShippingQueue
                shipmentNumber={this.state.returnNumber}
                isRefreshing={this.state.isRefreshing}
                addedBy={this.state?.user?._id}
                status="pending"
                isReturn
                handleCloseModal={this.props.handleCloseModal}
                onReturnTransferred={this.getReturnNumber}
              />
            </div>
          </div>
        )}
      </>
    );
  }
}

const mapStateToProps = (state) => {
  const { defaultLanguage } = state.Language;
  return {
    defaultLanguage,
  };
};

export default connect(mapStateToProps)(ReturnRequest);
